import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/store';
import { adminApi } from '@/services/api';
import Button from '@/components/ui/Button';
import { 
  BuildingStorefrontIcon, 
  MagnifyingGlassIcon, 
  FunnelIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  StarIcon,
  ClockIcon,
  UserIcon,
  SparklesIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface Shop {
  id: number;
  name: string;
  description: string;
  category_id: number;
  category?: {
    id: number;
    name: string;
  };
  owner_id: number;
  owner?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  };
  is_public: boolean;
  admin_rating?: number;
  priority_rank?: 'premium' | 'general' | 'basic';
  priority_score?: number;
  is_premium?: boolean;
  created_at: string;
  updated_at: string;
}

interface ShopFilters {
  is_public?: boolean;
  category_id?: number;
  search?: string;
  admin_rating?: number;
}

const ShopManagementPage: React.FC = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const [shops, setShops] = useState<Shop[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedShop, setSelectedShop] = useState<Shop | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<ShopFilters>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [updatingPriority, setUpdatingPriority] = useState<number | null>(null);

  useEffect(() => {
    fetchShops();
    fetchCategories();
  }, [filters]);

  // Debug: Log when selectedShop changes
  useEffect(() => {
    if (selectedShop) {
      console.log('selectedShop changed:', selectedShop);
      console.log('Priority values:', {
        priority_rank: selectedShop.priority_rank,
        priority_score: selectedShop.priority_score,
        is_premium: selectedShop.is_premium
      });
    }
  }, [selectedShop]);

  const fetchShops = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await adminApi.getAllShops();
      let filteredShops = response.data;

      // Apply filters
      if (filters.is_public !== undefined) {
        filteredShops = filteredShops.filter((shop: Shop) => shop.is_public === filters.is_public);
      }
      if (filters.category_id) {
        filteredShops = filteredShops.filter((shop: Shop) => shop.category_id === filters.category_id);
      }
      if (filters.admin_rating) {
        filteredShops = filteredShops.filter((shop: Shop) => (shop.admin_rating || 0) >= filters.admin_rating!);
      }
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        filteredShops = filteredShops.filter((shop: Shop) =>
          shop.name.toLowerCase().includes(searchLower) ||
          shop.description.toLowerCase().includes(searchLower) ||
          shop.owner?.first_name.toLowerCase().includes(searchLower) ||
          shop.owner?.last_name.toLowerCase().includes(searchLower)
        );
      }

      setShops(filteredShops);
    } catch (error: any) {
      setError(error.detail || 'Failed to fetch shops');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await adminApi.getAllCategories();
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const handleSearch = () => {
    setFilters(prev => ({ ...prev, search: searchQuery }));
  };

  const handleFilterChange = (key: keyof ShopFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({});
    setSearchQuery('');
  };

  const handleApproveShop = async (shopId: number) => {
    try {
      await adminApi.approveShop(shopId);
      await fetchShops();
      setSelectedShop(null);
    } catch (error: any) {
      setError(error.detail || 'Failed to approve shop');
    }
  };

  const handleRejectShop = async (shopId: number, reason: string) => {
    try {
      await adminApi.rejectShop(shopId, reason);
      await fetchShops();
      setSelectedShop(null);
    } catch (error: any) {
      setError(error.detail || 'Failed to reject shop');
    }
  };

  const handleRateShop = async (shopId: number, rating: number) => {
    try {
      await adminApi.rateShop(shopId, rating);
      await fetchShops();
      setSelectedShop(null);
    } catch (error: any) {
      setError(error.detail || 'Failed to rate shop');
    }
  };

  const handleUpdatePriority = async (
    shopId: number,
    priority_rank?: 'premium' | 'general' | 'basic',
    priority_score?: number,
    is_premium?: boolean
  ) => {
    try {
      setUpdatingPriority(shopId);
      
      console.log('Updating priority with:', { shopId, priority_rank, priority_score, is_premium });
      
      const response = await adminApi.updateShopPriority(shopId, {
        priority_rank,
        priority_score,
        is_premium
      });
      
      console.log('Full API response:', response);
      console.log('Response data:', response.data);
      
      // The API returns { message: "...", shop: { id, name, priority_rank, priority_score, is_premium } }
      const shopData = response.data?.shop || response.data;
      
      console.log('Full response:', response);
      console.log('Response data:', response.data);
      console.log('Shop data:', shopData);
      
      // Use the values directly from the API response (these are the actual database values)
      // If shopData doesn't exist, use the values we sent (optimistic update)
      const newPriorityRank = shopData?.priority_rank ?? priority_rank ?? selectedShop?.priority_rank ?? 'general';
      const newPriorityScore = shopData?.priority_score ?? priority_score ?? selectedShop?.priority_score ?? 0;
      const newIsPremium = shopData?.is_premium ?? is_premium ?? selectedShop?.is_premium ?? false;
      
      console.log('Final values to set:', { newPriorityRank, newPriorityScore, newIsPremium });
      
      toast.success('Shop priority updated successfully!');
      
      // Update selectedShop immediately with the new values
      if (selectedShop && selectedShop.id === shopId) {
        const updatedShop = {
          ...selectedShop,
          priority_rank: newPriorityRank,
          priority_score: newPriorityScore,
          is_premium: newIsPremium,
        };
        console.log('Immediately updating selectedShop to:', updatedShop);
        setSelectedShop(updatedShop);
      }
      
      // Update the shops list
      setShops(prevShops => 
        prevShops.map(shop => 
          shop.id === shopId 
            ? {
                ...shop,
                priority_rank: newPriorityRank,
                priority_score: newPriorityScore,
                is_premium: newIsPremium,
              }
            : shop
        )
      );
      
      // Refetch to ensure we have the latest data (async, doesn't block UI)
      fetchShops().then(() => {
        // After refetch, update selectedShop if it's still the same shop
        if (selectedShop && selectedShop.id === shopId) {
          setShops(currentShops => {
            const refreshedShop = currentShops.find(s => s.id === shopId);
            if (refreshedShop) {
              setSelectedShop({ ...refreshedShop });
            }
            return currentShops;
          });
        }
      });
    } catch (error: any) {
      console.error('Error updating shop priority:', error);
      console.error('Error response:', error.response);
      toast.error(error.response?.data?.detail || error.message || 'Failed to update shop priority');
    } finally {
      setUpdatingPriority(null);
    }
  };

  const getStatusColor = (isPublic: boolean) => {
    return isPublic ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800';
  };

  const getStatusLabel = (isPublic: boolean) => {
    return isPublic ? 'Approved' : 'Pending';
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <StarIcon
        key={i}
        className={`h-4 w-4 ${
          i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
        }`}
      />
    ));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading shops...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Shop Management</h1>
              <p className="mt-2 text-gray-600">
                Approve, manage, and rate shops on your platform
              </p>
              <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-gray-700">
                  <SparklesIcon className="h-4 w-4 inline mr-1 text-yellow-600" />
                  <strong>Priority & Ranking:</strong> Click "View Details" or "Priority" button on any shop to access Priority & Ranking settings. 
                  Set shops as Premium, adjust priority scores (0-1000), and control visibility.
                </p>
              </div>
            </div>
            <div className="flex space-x-3">
              <Button variant="outline" onClick={() => setShowFilters(!showFilters)}>
                <FunnelIcon className="h-5 w-5 mr-2" />
                Filters
              </Button>
              <Button onClick={fetchShops}>
                Refresh
              </Button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md mb-6">
            {error}
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
          {/* Search Bar */}
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search shops by name, description, or owner..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <Button onClick={handleSearch} className="px-6">
              Search
            </Button>
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="border-t pt-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={filters.is_public === undefined ? '' : filters.is_public.toString()}
                    onChange={(e) => handleFilterChange('is_public', e.target.value === '' ? undefined : e.target.value === 'true')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">All Status</option>
                    <option value="false">Pending</option>
                    <option value="true">Approved</option>
                  </select>
                </div>

                {/* Category Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    value={filters.category_id || ''}
                    onChange={(e) => handleFilterChange('category_id', e.target.value ? Number(e.target.value) : undefined)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">All Categories</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Rating Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Minimum Rating
                  </label>
                  <select
                    value={filters.admin_rating || ''}
                    onChange={(e) => handleFilterChange('admin_rating', e.target.value ? Number(e.target.value) : undefined)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Any Rating</option>
                    <option value="1">1+ Stars</option>
                    <option value="2">2+ Stars</option>
                    <option value="3">3+ Stars</option>
                    <option value="4">4+ Stars</option>
                    <option value="5">5 Stars</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end mt-4">
                <Button variant="outline" onClick={clearFilters}>
                  Clear Filters
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Shops Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {shops.map((shop) => (
            <div key={shop.id} className="bg-white rounded-lg shadow-sm border overflow-hidden hover:shadow-md transition-shadow">
              {/* Shop Header */}
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{shop.name}</h3>
                    <p className="text-sm text-gray-600 line-clamp-2">{shop.description}</p>
                  </div>
                  <div className="flex-shrink-0 ml-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(shop.is_public)}`}>
                      {getStatusLabel(shop.is_public)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Shop Details */}
              <div className="p-6">
                <div className="space-y-3">
                  {/* Owner */}
                  {shop.owner && (
                    <div className="flex items-center space-x-2">
                      <UserIcon className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        Owner: {shop.owner.first_name} {shop.owner.last_name}
                      </span>
                    </div>
                  )}

                  {/* Category */}
                  {shop.category && (
                    <div className="flex items-center space-x-2">
                      <BuildingStorefrontIcon className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        Category: {shop.category.name}
                      </span>
                    </div>
                  )}

                  {/* Admin Rating */}
                  <div className="flex items-center space-x-2">
                    <StarIcon className="h-4 w-4 text-gray-400" />
                    <div className="flex items-center space-x-1">
                      {renderStars(shop.admin_rating || 0)}
                      <span className="text-sm text-gray-600 ml-1">
                        ({shop.admin_rating || 0}/5)
                      </span>
                    </div>
                  </div>

                  {/* Priority/Ranking */}
                  <div className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                    <div className="flex items-center space-x-2">
                      {shop.is_premium ? (
                        <SparklesIcon className="h-4 w-4 text-yellow-500" />
                      ) : (
                        <StarIcon className="h-4 w-4 text-gray-400" />
                      )}
                      <span className="text-sm font-medium text-gray-700 capitalize">
                        {shop.priority_rank || 'general'}
                      </span>
                      {shop.priority_score !== undefined && (
                        <span className="text-xs text-gray-500">
                          (Score: {shop.priority_score})
                        </span>
                      )}
                    </div>
                    {shop.is_premium && (
                      <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-medium rounded">
                        Premium
                      </span>
                    )}
                  </div>

                  {/* Created Date */}
                  <div className="flex items-center space-x-2">
                    <ClockIcon className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-600">
                      Created: {new Date(shop.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-between items-center mt-6 space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedShop(shop)}
                    className="flex-1"
                  >
                    <EyeIcon className="h-4 w-4 mr-2" />
                    View Details
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedShop(shop)}
                    className="flex-1 border-yellow-300 text-yellow-700 hover:bg-yellow-50"
                    title="Manage Priority & Ranking"
                  >
                    <SparklesIcon className="h-4 w-4 mr-2" />
                    Priority
                  </Button>
                  
                  {!shop.is_public && (
                    <Button
                      size="sm"
                      onClick={() => handleApproveShop(shop.id)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircleIcon className="h-4 w-4 mr-2" />
                      Approve
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {shops.length === 0 && (
          <div className="text-center py-12">
            <BuildingStorefrontIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No shops found</h3>
            <p className="text-gray-600">Try adjusting your search or filter criteria.</p>
          </div>
        )}

        {/* Shop Detail Modal */}
        {selectedShop && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-6 border w-full max-w-2xl shadow-lg rounded-md bg-white my-10">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-gray-900">Shop Details & Priority Management</h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedShop(null)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ×
                  </Button>
                </div>
                
                <div className="space-y-6 max-h-[75vh] overflow-y-auto pr-2">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">{selectedShop.name}</h4>
                    <p className="text-gray-600 mt-1">{selectedShop.description}</p>
                  </div>

                  {selectedShop.owner && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Owner</label>
                      <p className="text-gray-900">
                        {selectedShop.owner.first_name} {selectedShop.owner.last_name}
                      </p>
                      <p className="text-sm text-gray-600">{selectedShop.owner.email}</p>
                    </div>
                  )}

                  {selectedShop.category && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Category</label>
                      <p className="text-gray-900">{selectedShop.category.name}</p>
                    </div>
                  )}

                  <div className="flex items-center space-x-2">
                    <label className="block text-sm font-medium text-gray-700">Admin Rating:</label>
                    <div className="flex items-center space-x-1">
                      {renderStars(selectedShop.admin_rating || 0)}
                      <span className="text-sm text-gray-600 ml-1">
                        ({selectedShop.admin_rating || 0}/5)
                      </span>
                    </div>
                  </div>

                  {/* Priority Management */}
                  <div className="border-t-2 border-yellow-200 pt-6 mt-6 bg-yellow-50 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-4">
                      <SparklesIcon className="h-5 w-5 text-yellow-600" />
                      <h4 className="text-lg font-bold text-gray-900">Priority & Ranking Settings</h4>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                      Control shop visibility and ranking. Premium shops appear first to buyers.
                    </p>
                    <div className="space-y-3">
                      {/* Priority Rank */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Priority Rank
                        </label>
                        <select
                          key={`rank-${selectedShop.id}-${selectedShop.priority_rank}`}
                          value={selectedShop.priority_rank ?? 'general'}
                          onChange={(e) => {
                            const newRank = e.target.value as 'premium' | 'general' | 'basic';
                            handleUpdatePriority(selectedShop.id, newRank);
                          }}
                          disabled={updatingPriority === selectedShop.id}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        >
                          <option value="basic">Basic</option>
                          <option value="general">General</option>
                          <option value="premium">Premium</option>
                        </select>
                      </div>

                      {/* Priority Score */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Priority Score (0-1000)
                        </label>
                        <div className="flex items-center space-x-2">
                          <input
                            key={`score-${selectedShop.id}-${selectedShop.priority_score}`}
                            type="number"
                            min="0"
                            max="1000"
                            value={selectedShop.priority_score ?? 0}
                            onChange={(e) => {
                              const score = parseInt(e.target.value) || 0;
                              handleUpdatePriority(selectedShop.id, undefined, score);
                            }}
                            disabled={updatingPriority === selectedShop.id}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          />
                          <button
                            onClick={() => handleUpdatePriority(selectedShop.id, undefined, Math.min((selectedShop.priority_score ?? 0) + 10, 1000))}
                            disabled={updatingPriority === selectedShop.id}
                            className="p-2 border border-gray-300 rounded-md hover:bg-gray-50"
                          >
                            <ArrowUpIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleUpdatePriority(selectedShop.id, undefined, Math.max((selectedShop.priority_score ?? 0) - 10, 0))}
                            disabled={updatingPriority === selectedShop.id}
                            className="p-2 border border-gray-300 rounded-md hover:bg-gray-50"
                          >
                            <ArrowDownIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      {/* Premium Toggle */}
                      <div className="flex items-center justify-between">
                        <label className="block text-sm font-medium text-gray-700">
                          Premium Status
                        </label>
                        <button
                          onClick={() => handleUpdatePriority(selectedShop.id, undefined, undefined, !selectedShop.is_premium)}
                          disabled={updatingPriority === selectedShop.id}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                            selectedShop.is_premium ? 'bg-yellow-500' : 'bg-gray-200'
                          }`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              selectedShop.is_premium ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>

                      {updatingPriority === selectedShop.id && (
                        <div className="text-sm text-gray-500">Updating...</div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Status</label>
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedShop.is_public)}`}>
                        {getStatusLabel(selectedShop.is_public)}
                      </span>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Created</label>
                      <p className="text-gray-900">{new Date(selectedShop.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <Button variant="outline" onClick={() => setSelectedShop(null)}>
                    Close
                  </Button>
                  
                  {!selectedShop.is_public && (
                    <Button
                      onClick={() => handleApproveShop(selectedShop.id)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircleIcon className="h-4 w-4 mr-2" />
                      Approve Shop
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShopManagementPage;
